import { useLazyQuery } from "@apollo/client";
import { MenuItem, Stack, TextField, Typography } from "@mui/material";
import { Dialog } from "components/Dialog";
import { FormTextField } from "components/Forms/FormTextField";
import { IssueListButton } from "components/Forms/IssueListButton";
import { safeParseFloat } from "encamp-shared/src/tierii/utilities";
import { Issue, IssueLevel, UnitType } from "generated-graphql/graphql";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useCallback, useMemo } from "react";
import { Controller, FormProvider } from "react-hook-form";
import { hasCriticalIssues } from "util/forms";

import { formatQuantity, UNIT_LABELS } from "../Containers/containerDisplay";
import { VALIDATE_UPDATE_SHIPMENT_LINES_INPUT } from "./api";
import { DisplayLine } from "./useStagedShipmentLines";

/**
 * The validator's input types the quantity as a required Float, so a value that
 * isn't a number can't be sent at all — it serializes to null and the query
 * errors, which would otherwise surface as "no issues" and leave Save enabled.
 */
const UNPARSEABLE_QUANTITY_ISSUE: Issue = {
  level: IssueLevel.Critical,
  key: "lineQuantityInvalid",
  message: "Enter the quantity as a number.",
  modelName: "WasteShipmentLine",
  dataName: null,
  jurisdictions: [],
};

type EditLineQuantityFormValues = {
  quantity: string;
  unit: UnitType;
};

/** The quantity form, validated through the same rule the save will run. */
function useLineQuantityForm(
  shipmentId: string,
  lineId: string,
  defaultValues: EditLineQuantityFormValues
) {
  const [validateQuery] = useLazyQuery(VALIDATE_UPDATE_SHIPMENT_LINES_INPUT, {
    fetchPolicy: "no-cache",
  });

  const validateInput = useCallback(
    async (values: EditLineQuantityFormValues): Promise<Issue[]> => {
      const quantity = safeParseFloat(values.quantity);
      if (quantity === null) {
        return [UNPARSEABLE_QUANTITY_ISSUE];
      }
      const { data } = await validateQuery({
        variables: {
          input: { shipmentId, setQuantities: [{ lineId, quantity, unit: values.unit }] },
        },
      });
      return data?.updateShipmentLinesInputValidator ?? [];
    },
    [validateQuery, shipmentId, lineId]
  );

  return useValidatingForm<EditLineQuantityFormValues>(defaultValues, [], validateInput);
}

/**
 * The container's own amount as a bound on this field, said on the field rather
 * than only behind the issue button — the number the reader just typed is where
 * the objection belongs.
 *
 * Only when the units agree. Across units the container's amount is not a bound
 * this field can state: that comparison needs the stream's density, which the
 * server has and this does not.
 */
function containerCapFor(
  line: DisplayLine & { lineId: string },
  unitValue: UnitType,
  quantityValue: string
) {
  const sameUnitCap =
    unitValue === line.container.amountUnit && line.container.amount != null
      ? line.container.amount
      : null;
  const typed = safeParseFloat(quantityValue);
  return {
    sameUnitCap,
    overContainer: sameUnitCap != null && typed != null && typed > sameUnitCap,
  };
}

export function EditLineQuantityDialog({
  shipmentId,
  line,
  onStage,
  onClose,
}: {
  shipmentId: string;
  line: DisplayLine & { lineId: string };
  onStage: (lineId: string, quantity: number, unit: UnitType) => void;
  onClose: () => void;
}) {
  const defaultValues = useMemo<EditLineQuantityFormValues>(
    () => ({ quantity: String(line.quantity), unit: line.unit }),
    [line]
  );

  const form = useLineQuantityForm(shipmentId, line.lineId, defaultValues);
  const {
    control,
    getValues,
    watch,
    formState: { isDirty },
    issues,
  } = form;
  const quantityValue = watch("quantity");
  const unitValue = watch("unit");

  const handleSave = () => {
    if (hasCriticalIssues(issues)) {
      return;
    }
    const values = getValues();
    const quantity = safeParseFloat(values.quantity);
    if (quantity === null) {
      return;
    }
    onStage(line.lineId, quantity, values.unit);
    onClose();
  };

  const { sameUnitCap, overContainer } = containerCapFor(line, unitValue, quantityValue);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="xs"
      title={`Quantity for ${line.container.containerLabel}`}
      additionalPrimaryActionElements={[<IssueListButton key="issues" issues={issues} />]}
      primaryActionButtonProps={{
        saveText: "Apply",
        disabled: hasCriticalIssues(issues) || !isDirty || safeParseFloat(quantityValue) === null,
        onClick: handleSave,
      }}
    >
      <FormProvider {...form}>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Ship less than the full container (partial ship) or set the amount leaving a tank.
          </Typography>
          <FormTextField
            control={control}
            name="quantity"
            label="Quantity"
            fullWidth
            type="number"
            // Capped only when the units agree. Across units the container's
            // amount is not a bound this field can state -- that comparison
            // needs the stream's density, which the server does.
            inputProps={{
              min: 0,
              step: "any",
              ...(sameUnitCap != null ? { max: sameUnitCap } : {}),
            }}
            helperText={
              overContainer
                ? `More than the container holds (${formatQuantity(line.container.amount, line.container.amountUnit)})`
                : `Container holds ${formatQuantity(line.container.amount, line.container.amountUnit)}`
            }
            textFieldProps={{ size: "small", error: overContainer }}
          />
          <Controller
            control={control}
            name="unit"
            render={({ field }) => (
              <TextField
                label="Unit"
                size="small"
                select
                value={field.value}
                onChange={(e) => field.onChange(e.target.value as UnitType)}
              >
                {Object.values(UnitType).map((u) => (
                  <MenuItem key={u} value={u}>
                    {UNIT_LABELS[u] ?? u}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </FormProvider>
    </Dialog>
  );
}
